import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { routes } from "../../constants/routes";
import { injectModels } from "../../redux/injectModels";
import {
  AuthService,
  EventService,
  RatingReviewService,
  UserProfileService,
} from "../../services";
import { doc, updateDoc } from "firebase/firestore";

import {
  getDb,
  getGroupMessagesThread,
  getMessageThreadFrom,
  getMessageThreadTo,
} from "../../services/FirebaseStorage";
import { UserRoleType } from "../../constants/Enum";

const MessageDashboard = ({ children, application }) => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const loginUser = useMemo(() => AuthService.getLoginUser(), []);
  const [messageThreads, setMessageThreads] = useState([]);
  const [groupMessageThreads, setGroupMessageThreads] = useState([]);
  const [activeThread, setActiveThread] = useState("");
  const [isLoading, setLoading] = useState(false);
  const baseUrl = process.env.REACT_APP_API_ENDPOINT?.replace("/api", "");
  const toUser = location.state?.toUser || null;

  // Safe navigation helper
  const safeGet = (obj, path, defaultValue = null) => {
    try {
      return (
        path.split(".").reduce((acc, part) => acc?.[part], obj) ?? defaultValue
      );
    } catch (error) {
      console.error("Safe navigation error:", error);
      return defaultValue;
    }
  };

  const addDefaultImg = (e, isGroup = false) => {
    if (e && e.target) {
      e.target.onerror = null;
      e.target.src = isGroup
        ? "/assets/images/users/multi-user.jpg"
        : "/assets/images/users/user-dummy-img.jpg";
    }
  };

  //handle message thread
  useEffect(() => {
    let unsubscribeFrom = null;
    let unsubscribeTo = null;

    const bootstrapAsync = async () => {
      try {
        if (!loginUser?.id) {
          console.warn("No login user found");
          setLoading(false);
          return;
        }

        setLoading(true);

        unsubscribeFrom = await getMessageThreadFrom(
          loginUser?.id,
          async (threadData1) => {
            unsubscribeTo = await getMessageThreadTo(
              loginUser?.id,
              async (threadData2) => {
                const combinedThreads = [
                  ...(threadData1 || []),
                  ...(threadData2 || []),
                ];
                await getMessages(combinedThreads);
              },
            );
          },
        );
      } catch (e) {
        console.error("ERROR_boot in message thread: ", e);
        setLoading(false);
      }
    };

    if (loginUser?.id) {
      bootstrapAsync();
    } else {
      setLoading(false);
    }

    return () => {
      if (unsubscribeFrom && typeof unsubscribeFrom === "function") {
        unsubscribeFrom();
      }
      if (unsubscribeTo && typeof unsubscribeTo === "function") {
        unsubscribeTo();
      }
    };
  }, [safeGet(location, "state.thread.id")]);

  //handle group messages thread
  useEffect(() => {
    let unsubscribe = null;

    const bootstrapAsync = async () => {
      try {
        if (!loginUser?.id) {
          console.warn("No login user found for group messages");
          setLoading(false);
          return;
        }

        setLoading(true);
        unsubscribe = await getGroupMessagesThread(
          loginUser?.id,
          async (threadData) => {
            if (threadData && Array.isArray(threadData)) {
              await getGroupMessages(threadData);
            } else {
              setLoading(false);
            }
          },
        );
      } catch (e) {
        console.error("ERROR_boot in group messages: ", e);
        setLoading(false);
      }
    };

    if (loginUser?.id) {
      bootstrapAsync();
    } else {
      setLoading(false);
    }

    return () => {
      if (unsubscribe && typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [safeGet(location, "state.thread.id")]);

  const getMessages = async (messageData) => {
    if (!messageData) {
      setMessageThreads([]);
      setLoading(false);
      return;
    }

    var isThreadExists = false;
    var tempThread = getTempThread();

    if (tempThread && toUser && !toUser?.isGroup) {
      if (messageData?.length > 0) {
        let threadData = messageData.filter(
          (thread) =>
            thread?.id && toUser?.userId && thread.id.includes(toUser.userId),
        );

        if (threadData?.length === 0) {
          if (tempThread?.toUser) {
            messageData.push(tempThread);
          }
        } else {
          tempThread = threadData[0];
          isThreadExists = true;
        }
      } else {
        if (tempThread?.toUser) {
          messageData.push(tempThread);
        }
      }
    } else if (id && id !== "undefined") {
      const db = await getDb();
      if (!db) {
        console.error("Database connection failed");
        setMessageThreads(messageData);
        setLoading(false);
        return;
      }

      var business = await getBusinessProfile();
      if (business != null && business?.userId) {
        tempThread = getTempThread(business);

        if (tempThread?.toUser) {
          let threadData = messageData.filter((thread) =>
            thread?.id?.includes(business?.userId),
          );

          if (threadData?.length === 0) {
            messageData.push(tempThread);
          } else {
            tempThread = threadData?.[0];
            isThreadExists = true;
          }

          // Update EO/Trader Name
          let fromName = tempThread?.fromName || "";
          let toName = tempThread?.toName || "";
          let title = tempThread?.toName || "";

          if (tempThread?.createdBy) {
            var fromUserProfile = await getPersonalProfile(
              tempThread?.createdBy,
            );
            if (fromUserProfile != null) {
              fromName =
                (fromUserProfile.firstName || "") +
                (fromUserProfile.lastName
                  ? " " + fromUserProfile.lastName
                  : "");
            }
          }

          if (tempThread?.toUser) {
            var toUserProfile = await getPersonalProfile(tempThread?.toUser);
            if (toUserProfile != null) {
              toName = title =
                (toUserProfile.firstName || "") +
                (toUserProfile.lastName ? " " + toUserProfile.lastName : "");
            }
          }

          try {
            const threadRef = await doc(
              db,
              `swallow_chat`,
              `${tempThread?.id}`,
            );
            if (threadRef) {
              await updateDoc(threadRef, {
                fromName: fromName || "",
                toName: toName || "",
                title: title || "",
              });
            }
          } catch (e) {
            console.error("Error updating thread names:", e);
          }
        }
      }
    }

    setMessageThreads(messageData || []);

    if ((toUser && !toUser?.isGroup) || id) {
      const threadId = tempThread?.id;
      if (threadId) {
        setActiveThread(threadId);
      }

      if (location.state?.isRedirect) {
        const redirectId = toUser ? toUser.userId : business?.userId;
        if (redirectId) {
          navigate(routes.MESSAGE_LIST.replace(":id", redirectId), {
            state: {
              isThreadExist: isThreadExists,
              thread: tempThread,
              isPreEventMessage: location.state?.isPreEventMessage || false,
              eventName: location.state?.eventName || "",
              isRedirect: false,
            },
          });
        }
      }
    }

    setLoading(false);
  };

  const getGroupMessages = async (messageData) => {
    if (!messageData) {
      setGroupMessageThreads([]);
      setLoading(false);
      return;
    }

    var isThreadExists = false;
    var tempThread = getGroupMessageTempThread();

    if (toUser && toUser?.isGroup) {
      if (messageData?.length > 0) {
        let threadData = messageData.filter(
          (thread) =>
            thread?.id && toUser?.eventId && thread.id.includes(toUser.eventId),
        );

        if (threadData?.length === 0) {
          if (tempThread?.users?.length > 0) {
            messageData.push(tempThread);
          }
        } else {
          tempThread = threadData[0];
          isThreadExists = true;
        }
      } else {
        if (tempThread?.users?.length > 0) {
          messageData.push(tempThread);
        }
      }
    } else if (id && id !== "undefined") {
      var business = await getEventProfiles();
      if (business != null && business?.eventId) {
        tempThread = getGroupMessageTempThread(business);

        if (tempThread?.id) {
          let threadData = messageData.filter(
            (thread) => thread?.id && thread.id.includes(business?.eventId),
          );

          if (threadData?.length === 0) {
            if (tempThread?.users?.length > 0) {
              messageData.push(tempThread);
            }
          } else {
            // Update user if not matched with approved users in group
            if (tempThread?.users?.length !== threadData[0]?.users?.length) {
              const db = await getDb();
              if (db) {
                let lastRead = {};
                const currentTime = new Date();
                tempThread.users.forEach((userId) => {
                  if (userId === loginUser?.id) {
                    lastRead[userId] = currentTime;
                  } else {
                    lastRead[userId] = new Date(
                      currentTime.getTime() - 1000 * 60,
                    );
                  }
                });

                try {
                  const threadRef = await doc(
                    db,
                    `swallow_group_chat`,
                    `${tempThread?.id}`,
                  );

                  if (threadRef) {
                    await updateDoc(threadRef, {
                      users: tempThread?.users || [],
                      lastRead: lastRead,
                      usersInfo: tempThread?.usersInfo || {},
                    });
                  }
                } catch (e) {
                  console.error("Error updating group thread:", e);
                }
                threadData[0] = tempThread;
              }
            }
            tempThread = threadData[0];
            isThreadExists = true;
          }
        }
      }
    }

    setGroupMessageThreads(messageData || []);

    if ((toUser && toUser?.isGroup) || id) {
      const threadId = tempThread?.id;
      if (threadId) {
        setActiveThread(threadId);
      }

      if (location.state?.isRedirect) {
        const redirectId = toUser ? toUser?.eventId : business?.eventId;
        if (redirectId) {
          navigate(routes.GROUP_MESSAGE_LIST.replace(":id", redirectId), {
            state: {
              isThreadExist: isThreadExists,
              thread: tempThread,
              isRedirect: false,
            },
          });
        }
      }
    }

    setLoading(false);
  };

  const getTempThread = (business = null) => {
    const targetUser = toUser || business;

    if (!loginUser?.id || !targetUser?.userId) {
      console.warn("Invalid thread data", { loginUser, targetUser });
      return null;
    }

    return {
      avatar: `${baseUrl || ""}/ImagesUpload/UserProfileImageUpload/${
        loginUser.id
      }.jpg`,
      toAvatar: getAvatarURL(targetUser),
      createdBy: loginUser.id,
      id: `${loginUser.id}-${targetUser.userId}`,
      title: targetUser.businessName || "Chat",
      fromName: loginUser.firstName || "User",
      toName: targetUser.businessName || "Business",
      toUser: targetUser.userId,
    };
  };

  const getGroupMessageTempThread = (business = null) => {
    if (!loginUser?.id) {
      console.warn("No login user found for group message thread");
      return null;
    }

    let avatar = toUser ? toUser?.eventAvatar : business?.eventAvatar;
    let title = toUser ? toUser?.eventName : business?.eventName;
    let users = [loginUser?.id];
    let usersInfo = {};

    let tempUsers = toUser
      ? toUser?.users ?? []
      : business?.approvedTraders ?? [];

    tempUsers.forEach((user) => {
      if (user?.traderId) {
        users.push(user?.traderId);
        usersInfo[user?.traderId] = {
          name: `${user?.firstName || ""} ${user?.lastName || ""}`,
          avatar: user?.businessProfileUrl || "",
        };
      }
    });

    usersInfo[loginUser?.id] = {
      name: toUser
        ? toUser?.organizerBusinessName || "Organizer"
        : business?.organizerBusinessName || "Organizer",
      avatar: avatar || "",
    };

    const eventId = toUser ? toUser?.eventId : business?.eventId;

    if (!eventId) {
      console.warn("No event ID found for group message thread");
      return null;
    }

    return {
      avatar: avatar || "",
      createdBy: loginUser?.id,
      title: title || "Group Chat",
      users: users,
      isGroup: true,
      id: eventId,
      usersInfo: usersInfo,
    };
  };

  const getEventProfiles = async () => {
    if (!id) {
      console.warn("No event ID provided");
      return null;
    }

    try {
      setLoading(true);
      const res = await EventService.GetApprovedTradersByEventId(id);
      if (res?.isSuccess && res?.data) {
        return res?.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching event profiles:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getPersonalProfile = async (userId) => {
    if (!userId) {
      console.warn("No user ID provided");
      return null;
    }

    try {
      setLoading(true);
      const res = await UserProfileService.getUserProfileById(userId);
      if (res?.isSuccess && res?.data) {
        return res?.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching personal profile:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getBusinessProfile = async () => {
    if (!id) {
      console.warn("No business ID provided");
      return null;
    }

    try {
      setLoading(true);
      const res = await RatingReviewService.getBusinessProfileById(id);
      if (res?.isSuccess && res?.data) {
        return res?.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching business profile:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getAvatar = (message, isGroup = false) => {
    if (!message) return "";

    if (isGroup) {
      return message?.avatar || "";
    }

    return message?.createdBy === loginUser?.id
      ? message?.toAvatar || ""
      : message?.avatar || "";
  };

  const checkForGroupMessageUnread = (thread) => {
    if (!thread || !thread.id || activeThread === thread.id) {
      return false;
    }

    if (!thread?.lastRead || !loginUser?.id) {
      return false;
    }

    let isUnreadFound = false;
    const currentUserLastRead = thread?.lastRead?.[loginUser?.id];

    if (currentUserLastRead && thread.users) {
      thread.users.forEach((user) => {
        if (user && user !== loginUser?.id) {
          const userLastRead = thread?.lastRead?.[user];
          if (
            userLastRead &&
            currentUserLastRead.seconds < userLastRead.seconds &&
            currentUserLastRead.seconds !== userLastRead.seconds
          ) {
            isUnreadFound = true;
          }
        }
      });
    }

    return isUnreadFound;
  };

  const getAvatarURL = (profile, isGroup = false) => {
    if (!profile) {
      return "";
    }

    if (isGroup) {
      return `${baseUrl || ""}/${id || ""}.jpg`;
    }

    const profileId =
      profile.userType === UserRoleType.CUSTOMER
        ? profile?.userId
        : profile?.id;

    if (!profileId) {
      return "";
    }

    const newProfileURL = `/${
      profile.userType === UserRoleType.CUSTOMER
        ? "ImagesUpload/UserProfileImageUpload"
        : "ImagesUpload/BusinessProfileImageUpload"
    }/${profileId}.jpg`;

    return `${baseUrl || ""}${newProfileURL}`;
  };

  // Early return if no base URL
  if (!baseUrl) {
    console.warn("Base URL not configured");
  }

  return (
    <React.Fragment>
      <section className="message-area chat">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="chat-area">
                {/* chatlist */}
                <div className="chatlist">
                  <div className="modal-dialog-scrollable">
                    <div className="modal-content">
                      <div className="modal-body">
                        {/* chat-list */}
                        <div className="chat-lists">
                          <div className="tab-content" id="myTabContent">
                            <div
                              className="tab-pane fade show active"
                              id="Open"
                              role="tabpanel"
                              aria-labelledby="Open-tab"
                            >
                              {/* chat-list */}
                              <h4
                                style={{
                                  color: "white",
                                  padding: 10,
                                  borderBottom: "1px solid",
                                }}
                              >
                                Inbox {isLoading ? "Loading..." : ""}
                              </h4>
                              <div className="chat-list">
                                {groupMessageThreads?.length > 0 && (
                                  <h5 style={{ color: "white", padding: 10 }}>
                                    Event Groups
                                  </h5>
                                )}

                                {groupMessageThreads.map((message, index) => (
                                  <Link
                                    key={index}
                                    className="d-flex align-items-center"
                                    to={routes.GROUP_MESSAGE_LIST.replace(
                                      ":id",
                                      message?.id || "",
                                    )}
                                    state={{
                                      isThreadExist: true,
                                      thread: message,
                                    }}
                                    onClick={() => {
                                      if (message?.id) {
                                        setActiveThread(message.id);
                                      }
                                    }}
                                    style={{
                                      backgroundColor:
                                        activeThread === message?.id
                                          ? "#5b5b5b"
                                          : "#8a8a8a",
                                      borderBottom: "1px solid gray",
                                    }}
                                  >
                                    <div className="flex-shrink-0">
                                      <img
                                        style={{
                                          width: 50,
                                          height: 50,
                                          borderRadius: 25,
                                        }}
                                        className="img-fluid"
                                        src={getAvatar(message, true)}
                                        alt="user img"
                                        onError={(e) => {
                                          addDefaultImg(e, true);
                                        }}
                                      />
                                      {checkForGroupMessageUnread(message) && (
                                        <span className="active"></span>
                                      )}
                                    </div>
                                    <div className="flex-grow-1 ms-3">
                                      <h3>{message?.title || "Group Chat"}</h3>
                                    </div>
                                  </Link>
                                ))}

                                {messageThreads?.length > 0 && (
                                  <h5 style={{ color: "white", padding: 10 }}>
                                    Others
                                  </h5>
                                )}

                                {messageThreads.map((message, index) => {
                                  const otherUserId =
                                    loginUser?.id === message?.toUser
                                      ? message?.createdBy
                                      : message?.toUser;

                                  return (
                                    <Link
                                      key={index}
                                      className="d-flex align-items-center"
                                      to={routes.MESSAGE_LIST.replace(
                                        ":id",
                                        otherUserId || "",
                                      )}
                                      state={{
                                        isThreadExist: true,
                                        thread: message,
                                      }}
                                      onClick={() => {
                                        if (message?.id) {
                                          setActiveThread(message?.id);
                                        }
                                      }}
                                      style={{
                                        backgroundColor:
                                          activeThread === message?.id
                                            ? "#5b5b5b"
                                            : "#8a8a8a",
                                        borderBottom: "1px solid gray",
                                      }}
                                    >
                                      <div className="flex-shrink-0">
                                        <img
                                          style={{
                                            width: 50,
                                            height: 50,
                                            borderRadius: 25,
                                          }}
                                          className="img-fluid"
                                          src={getAvatar(message)}
                                          alt="user img"
                                          onError={(e) => {
                                            addDefaultImg(e, false);
                                          }}
                                        />
                                        {message?.lastRead &&
                                          message?.lastRead?.[loginUser?.id]
                                            ?.seconds <
                                            message?.lastRead?.[
                                              loginUser?.id ===
                                              message?.createdBy
                                                ? message?.toUser
                                                : message?.createdBy
                                            ]?.seconds &&
                                          `${
                                            message?.lastRead?.[
                                              message?.createdBy
                                            ]?.seconds
                                          }` !==
                                            `${
                                              message?.lastRead?.[
                                                message?.toUser
                                              ]?.seconds
                                            }` && (
                                            <span className="active"></span>
                                          )}
                                      </div>
                                      <div className="flex-grow-1 ms-3">
                                        <h3>
                                          {message?.createdBy === loginUser?.id
                                            ? message?.toName || "User"
                                            : message?.fromName || "User"}
                                        </h3>
                                      </div>
                                    </Link>
                                  );
                                })}
                              </div>
                              {/* chat-list */}
                            </div>
                          </div>
                        </div>
                        {/* chat-list */}
                      </div>
                    </div>
                  </div>
                </div>
                {/* chatlist */}

                <div className="chatbox">{children}</div>
              </div>
              {/* chatbox */}
            </div>
          </div>
        </div>
      </section>
    </React.Fragment>
  );
};

export default injectModels(["application"])(MessageDashboard);
